import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FlaskConical,
  Package,
  Scale,
  Building2,
  Sparkles,
  Server,
  Rocket,
  Microscope,
  GraduationCap,
  Users,
  Cpu,
  DollarSign,
  BookOpen,
  Trophy,
  ArrowRight,
  Check,
  X,
  Award,
  Calendar,
  Send,
  Loader
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= APPLICATION MODAL =============
const ApplicationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    background: '',
    track: '',
    linkedin: '',
    portfolio: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.background || !formData.track) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'AI Fellowship Application',
          message: `AI Fellowship Application\n\nPreferred Track: ${formData.track}\nBackground: ${formData.background}\n\nLinkedIn: ${formData.linkedin || 'Not provided'}\nPortfolio: ${formData.portfolio || 'Not provided'}\n\nWhy I want to join:\n${formData.message || 'No additional message'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Application submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', background: '', track: '', linkedin: '', portfolio: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Application error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', background: '', track: '', linkedin: '', portfolio: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-teal-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-teal-500/20 border border-purple-500/30">
              <Check size={32} className="text-purple-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Application Received!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for applying to the AI Fellowship. Our selection committee will review your application and respond within 1-2 weeks.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-500/20 to-teal-500/20 border border-purple-500/30 rounded-full">
              <Award size={14} className="text-purple-400" />
              <span className="text-xs text-purple-400 font-semibold">12-Month Program</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Apply to Fellowship</h2>
            <p className="text-purple-400 mb-6">Join the next cohort of AI innovators</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Background *</label>
                <select
                  value={formData.background}
                  onChange={(e) => handleChange('background', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select your background</option>
                  <option value="AI/ML Researcher" className="bg-slate-950">AI/ML Researcher</option>
                  <option value="ML Engineer" className="bg-slate-950">ML Engineer</option>
                  <option value="Software Engineer" className="bg-slate-950">Software Engineer</option>
                  <option value="Data Scientist" className="bg-slate-950">Data Scientist</option>
                  <option value="PhD Student/Candidate" className="bg-slate-950">PhD Student/Candidate</option>
                  <option value="Startup Founder" className="bg-slate-950">Startup Founder</option>
                  <option value="Graduate Student" className="bg-slate-950">Graduate Student</option>
                  <option value="Other" className="bg-slate-950">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Preferred Track *</label>
                <select
                  value={formData.track}
                  onChange={(e) => handleChange('track', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select your preferred track</option>
                  <option value="Research & Innovation" className="bg-slate-950">Research & Innovation</option>
                  <option value="Product Development" className="bg-slate-950">Product Development</option>
                  <option value="AI Safety & Ethics" className="bg-slate-950">AI Safety & Ethics</option>
                  <option value="Applied ML Industry" className="bg-slate-950">Applied ML Industry</option>
                  <option value="Generative AI" className="bg-slate-950">Generative AI</option>
                  <option value="AI Infrastructure" className="bg-slate-950">AI Infrastructure</option>
                  <option value="AI Entrepreneurship" className="bg-slate-950">AI Entrepreneurship</option>
                  <option value="Interdisciplinary AI" className="bg-slate-950">Interdisciplinary AI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">LinkedIn Profile</label>
                <input
                  type="url"
                  value={formData.linkedin}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Portfolio/GitHub</label>
                <input
                  type="url"
                  value={formData.portfolio}
                  onChange={(e) => handleChange('portfolio', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
                  placeholder="https://github.com/yourusername"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Why do you want to join? *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition resize-none"
                  placeholder="Tell us about your AI journey and goals..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-purple-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Application
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onApply }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-purple-400 transition">Home</Link>
            <a href="#tracks" className="text-sm text-gray-300 hover:text-purple-400 transition">Tracks</a>
            <a href="#benefits" className="text-sm text-gray-300 hover:text-purple-400 transition">Benefits</a>
            <a href="#journey" className="text-sm text-gray-300 hover:text-purple-400 transition">Journey</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onApply}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-purple-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-purple-500/50 transition"
          >
            Apply Now
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-80 h-80 bg-blue-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onApply }) => {
  const stats = [
    { number: "12", label: "Month Program" },
    { number: "50", label: "Fellows Per Cohort" },
    { number: "20+", label: "Expert Mentors" },
    { number: "Elite", label: "Selection Process" }
  ];

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-20 bg-slate-950">
      <GradientOrbs />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(139,92,246,0.15),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badge */}
        <motion.div
          className="mb-6 inline-flex items-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <span className="text-xs font-semibold text-purple-300 px-4 py-2 rounded-full border border-purple-500/50 bg-purple-500/10 backdrop-blur-sm flex items-center gap-2">
            <Award size={14} />
            FELLOWSHIP PROGRAM
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="block bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            AI Fellowship
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-teal-300 mb-4 font-medium"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Elite Community of AI Innovators
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Join an exclusive network of AI practitioners, researchers, and visionaries pushing the boundaries 
          of artificial intelligence. Collaborate on cutting-edge projects, access mentorship from industry 
          leaders, and shape the future of AI through intensive cohort-based learning and real-world impact.
        </motion.p>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-purple-500/30 transition-all duration-300"
              whileHover={{ y: -4, scale: 1.02 }}
            >
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                {stat.number}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(139, 92, 246, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={onApply}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold shadow-[0_0_30px_rgba(139,92,246,0.2)] transition flex items-center gap-2"
          >
            Apply to Fellowship <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('tracks').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-purple-500/40"
          >
            Explore Tracks
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= TRACK CARD =============
const TrackCard = ({ track, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-50px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-teal-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 hover:border-purple-500/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

        <div className="relative z-10 flex flex-col h-full">
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="text-xs font-bold text-purple-400 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20">
              Track {String(index + 1).padStart(2, '0')}
            </span>
          </div>

          <motion.div
            className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-teal-500/20 border border-purple-500/20 group-hover:from-purple-500/30 group-hover:to-teal-500/30 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <track.icon className="text-purple-300 group-hover:text-purple-200 transition" size={28} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
            {track.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed text-sm flex-grow">
            {track.description}
          </p>

          <ul className="space-y-2">
            {track.topics.map((topic, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-500 flex items-start gap-2"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{topic}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= TRACKS SECTION =============
const TracksSection = () => {
  const tracks = [
    {
      icon: FlaskConical,
      title: "Research & Innovation",
      description: "Conduct original AI research, publish papers, and contribute to the advancement of machine learning theory and applications.",
      topics: [
        "Novel algorithm development",
        "Academic paper writing and publication",
        "Conference presentation skills",
        "Collaboration with research institutions"
      ]
    },
    {
      icon: Package,
      title: "Product Development",
      description: "Build production-ready AI systems and launch innovative products that solve real-world problems at scale.",
      topics: [
        "End-to-end ML product development",
        "Production deployment and scaling",
        "User-centered AI design",
        "Go-to-market strategies for AI products"
      ]
    },
    {
      icon: Scale,
      title: "AI Safety & Ethics",
      description: "Focus on responsible AI development, alignment research, and building safe, ethical AI systems for society.",
      topics: [
        "AI alignment and safety research",
        "Bias detection and mitigation",
        "Ethical framework development",
        "Policy and governance considerations"
      ]
    },
    {
      icon: Building2,
      title: "Applied ML Industry",
      description: "Deploy machine learning solutions across healthcare, finance, climate, and other critical industries.",
      topics: [
        "Domain-specific AI applications",
        "Industry partnership projects",
        "Regulatory and compliance navigation",
        "Impact measurement and reporting"
      ]
    },
    {
      icon: Sparkles,
      title: "Generative AI",
      description: "Master large language models, diffusion systems, and multimodal AI to create next-generation generative applications.",
      topics: [
        "LLM fine-tuning and deployment",
        "Multimodal model development",
        "Creative AI applications",
        "Prompt engineering at scale"
      ]
    },
    {
      icon: Server,
      title: "AI Infrastructure",
      description: "Build scalable ML platforms, optimize training pipelines, and develop infrastructure for AI systems.",
      topics: [
        "MLOps and model deployment",
        "Distributed training systems",
        "Model optimization and efficiency",
        "Cloud and edge AI architecture"
      ]
    },
    {
      icon: Rocket,
      title: "AI Entrepreneurship",
      description: "Launch AI startups, secure funding, and build companies that leverage artificial intelligence for disruption.",
      topics: [
        "AI startup ideation and validation",
        "Fundraising and pitch development",
        "Team building and hiring",
        "Market strategy and growth"
      ]
    },
    {
      icon: Microscope,
      title: "Interdisciplinary AI",
      description: "Bridge AI with other fields like neuroscience, biology, physics, and social sciences for breakthrough discoveries.",
      topics: [
        "AI for scientific discovery",
        "Cross-domain collaboration",
        "Novel application development",
        "Interdisciplinary research methods"
      ]
    }
  ];

  return (
    <section id="tracks" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-gradient-to-b from-purple-950/20 to-transparent" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-purple-500/[0.03] rounded-full blur-[120px]" />
      <div className="absolute bottom-40 left-0 w-[300px] h-[300px] bg-teal-600/[0.03] rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-[10px] text-purple-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-purple-400" />
            Fellowship Tracks
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Specialization{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-400">Pathways</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Deep dive into specialized AI domains with expert guidance, hands-on projects, and peer collaboration. 
            Build breakthrough innovations while advancing the state-of-the-art in your chosen field.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tracks.map((track, index) => (
            <TrackCard key={index} track={track} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: GraduationCap,
      title: "Expert Mentorship",
      description: "One-on-one guidance from AI researchers, engineers, and founders at leading tech companies and research labs. Get personalized career and technical mentorship."
    },
    {
      icon: Users,
      title: "Elite Network",
      description: "Connect with exceptional AI practitioners worldwide. Build lasting relationships with peers who become co-founders, collaborators, and lifelong friends."
    },
    {
      icon: Cpu,
      title: "Compute Resources",
      description: "Access powerful GPU clusters and cloud computing credits to train large models and run experiments without infrastructure constraints."
    },
    {
      icon: DollarSign,
      title: "Project Funding",
      description: "Receive grants and resources to bring your AI projects to life. Fellows get funding for research, prototypes, and startup initiatives."
    },
    {
      icon: BookOpen,
      title: "Learning Resources",
      description: "Exclusive access to workshops, seminars, and courses from world-class AI experts. Stay at the cutting edge of rapidly evolving AI technology."
    },
    {
      icon: Trophy,
      title: "Career Acceleration",
      description: "Direct pathways to top AI roles at leading companies, research positions, or startup founding opportunities with ongoing alumni support."
    }
  ];

  return (
    <section id="benefits" className="relative py-24 px-4 overflow-hidden bg-slate-900/50">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.05),transparent_70%)]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-[10px] text-purple-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-purple-400" />
            Fellowship Benefits
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What Fellows{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-400">Receive</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Access exclusive resources, mentorship, and opportunities available only to AI Fellowship members. 
            Build your career while contributing to the advancement of artificial intelligence.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300 h-full">
                <motion.div
                  className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-teal-500/20 border border-purple-500/20 group-hover:from-purple-500/30 group-hover:to-teal-500/30 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <benefit.icon className="text-purple-300 group-hover:text-purple-200 transition" size={26} />
                </motion.div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-teal-400 transition-all">
                  {benefit.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= JOURNEY SECTION (Timeline) =============
const JourneySection = () => {
  const quarters = [
    {
      title: "Quarter 1: Foundation",
      description: "Intensive bootcamp covering advanced AI concepts, tool mastery, and team formation. Choose specialization track and begin first project with mentor guidance."
    },
    {
      title: "Quarter 2: Building",
      description: "Deep dive into specialization with hands-on projects, research, or product development. Collaborate with peers, present progress, and iterate based on expert feedback."
    },
    {
      title: "Quarter 3: Advancement",
      description: "Scale your work with advanced techniques, industry partnerships, or research collaborations. Begin preparing papers, products, or ventures for launch and public release."
    },
    {
      title: "Quarter 4: Launch",
      description: "Finalize and launch your capstone project - whether research publication, product release, or startup founding. Join the elite alumni network and access ongoing career support."
    }
  ];

  return (
    <section id="journey" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-purple-500/[0.03] rounded-full blur-[150px]" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-[10px] text-purple-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-purple-400" />
            Fellowship Journey
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            12-Month{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-400">Transformation</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Structured progression through intensive learning, building, and launching phases. 
            Each quarter focuses on developing specific skills and delivering tangible outcomes.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-purple-500 via-teal-500 to-emerald-500" />

          {quarters.map((quarter, index) => (
            <motion.div
              key={index}
              className={`relative flex items-start gap-8 mb-12 last:mb-0 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-teal-500 border-4 border-slate-950 z-10" />

              <div className={`ml-16 md:ml-0 md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                <motion.div
                  className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300"
                  whileHover={{ y: -4 }}
                >
                  <div className={`mb-3 ${index % 2 === 0 ? 'md:flex md:justify-end' : ''}`}>
                    <span className="text-xs font-bold text-purple-400 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 inline-flex items-center gap-1">
                      <Calendar size={12} />
                      Q{index + 1}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">
                    {quarter.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {quarter.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= IDEAL CANDIDATE SECTION =============
const IdealCandidateSection = () => {
  const candidates = [
    { emoji: "🔬", title: "AI Researchers", desc: "Publishing and pushing boundaries of ML" },
    { emoji: "👩‍💻", title: "ML Engineers", desc: "Building production AI systems at scale" },
    { emoji: "🚀", title: "AI Founders", desc: "Starting companies in the AI space" },
    { emoji: "🎓", title: "PhD Students", desc: "Advancing AI through academic research" }
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden bg-slate-900/50">
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Who Should Apply?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            We're looking for exceptional individuals passionate about AI
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {candidates.map((candidate, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 text-center hover:border-purple-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div className="text-4xl mb-3">{candidate.emoji}</div>
              <h3 className="text-white font-semibold mb-2">{candidate.title}</h3>
              <p className="text-gray-500 text-sm">{candidate.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onApply }) => {
  return (
    <section className="relative py-28 px-6 overflow-hidden bg-slate-950">
      <div className="max-w-3xl mx-auto">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[300px] bg-gradient-to-r from-purple-500/10 via-teal-500/10 to-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <motion.div
          className="relative text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/20 to-teal-500/20 border border-purple-500/20"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <Award className="text-purple-300" size={40} />
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight">
            Apply to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-400">AI Fellowship</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto leading-relaxed">
            Applications open quarterly. Join the next cohort of exceptional AI innovators building the future.
          </p>

          {/* What's Included */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {["Elite Mentorship", "Compute Credits", "Project Funding", "Alumni Network"].map((item, i) => (
              <span key={i} className="text-sm text-gray-400 flex items-center gap-2">
                <Check size={16} className="text-emerald-400" />
                {item}
              </span>
            ))}
          </div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(139, 92, 246, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={onApply}
              className="px-10 py-4 bg-gradient-to-r from-purple-500 via-teal-500 to-emerald-500 text-white rounded-full font-semibold text-lg hover:from-purple-400 hover:via-teal-400 hover:to-emerald-400 transition-all flex items-center justify-center gap-2"
            >
              Apply Now <ArrowRight size={20} />
            </motion.button>
            <Link to="/training">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 border border-white/20 rounded-full text-white font-semibold text-lg hover:bg-white/10 hover:border-purple-500/40 transition-all"
              >
                View All Programs
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/5 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4">Building the future of AI, together.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Programs</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-fundamentals" className="hover:text-purple-400 transition">AI Fundamentals</Link></li>
              <li><Link to="/corporate-ai-training" className="hover:text-purple-400 transition">Corporate Training</Link></li>
              <li><Link to="/ai-fellowship" className="hover:text-purple-400 transition">AI Fellowship</Link></li>
              <li><Link to="/student-program" className="hover:text-purple-400 transition">Student Program</Link></li>
              <li><Link to="/prompt-engineering" className="hover:text-purple-400 transition">Prompt Engineering</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-purple-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-purple-400 transition">All Programs</Link></li>
              <li><Link to="/#contact" className="hover:text-purple-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-purple-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-purple-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-purple-400 transition">Refund Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AIFellowshipPage() {
  const [showModal, setShowModal] = useState(false);

  const handleApply = () => {
    setShowModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onApply={handleApply} />
      <Hero onApply={handleApply} />
      <TracksSection />
      <BenefitsSection />
      <IdealCandidateSection />
      <JourneySection />
      <FinalCTA onApply={handleApply} />
      <Footer />
      
      <ApplicationModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
      />
    </div>
  );
}